// Copyright (C) 2021 Bystronic Laser AG

import { EChartsOption, SeriesOption } from 'echarts';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { GeneratorParams } from './generatorParams';
import {
  ChartGenerator,
  ProcedureName,
} from '@/models/Charts/abstract/chartGenerator';
import {
  BendFlowSpeedStatus,
  BendProductionModeStatus,
  BendStateDurationData,
  BendStateOverview,
  BendStatesOverviewData,
} from '@/models/Charts/chartsData';
import { getBendFlowSpeedColor, getBendStateColor } from '@/utils/color';
import { formatPercentage } from '@/utils/number';
import Color from 'color';
import { metricsService } from '@/services/metrics.service';

/**
 * Chart generator for the 'Status overview' widget.
 *
 * Returns Echarts options to draw a one-dimensional heatmap chart where each
 * square represents the value for a certain date.
 *
 * The colors of the squares range from lighter (higher values) to darker (lower
 * values).
 */
export default class BendStatusOverviewChartGenerator extends ChartGenerator<BendStatesOverviewData> {
  constructor(procedure: ProcedureName, private customerIdDh: number) {
    super(procedure);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
  ): Promise<BendStatesOverviewData | null> {
    return metricsService.getBendingDevicesMetrics<BendStatesOverviewData>(
      this.procedure,
      {
        customerIdDh: this.customerIdDh,
        timeSpan: timeSpan as FilterTimeSpanEnum,
        shiftIds: selectedShifts,
        deviceStringIds: selectedDevices,
      },
      this.controller,
    );
  }

  override updateOptions(
    data: BendStatesOverviewData,
    parameters: GeneratorParams = { paramValues: { chart: BendStateOverview } },
  ): EChartsOption {
    return {
      title: {
        show: false,
      },
      grid: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#f6f8fc',
        borderWidth: 0,
        show: true,
      },
      xAxis: {
        type: 'value',
        show: false,
      },
      yAxis: {
        type: 'category',
        show: false,
      },
      tooltip: {
        show: false,
      },
      series: this.generateSeries(data, parameters.paramValues.chart),
    };
  }

  private generateSeries(
    data: BendStatesOverviewData,
    chart: BendStateOverview,
  ): SeriesOption[] {
    if (chart === BendStateOverview.ProductionMode) {
      return this.createSeries(
        data.productionMode.filter((x) => !x.isPrevious),
        getBendStateColor,
      );
    }
    return this.createSeries(
      data.flowMode.filter((x) => !x.isPrevious),
      getBendFlowSpeedColor,
    );
  }

  private createSeries<
    T extends BendProductionModeStatus | BendFlowSpeedStatus,
  >(
    statesData: BendStateDurationData<T>[],
    getColor: (state: T) => string,
  ): SeriesOption[] {
    return statesData.map((item) => {
      const color = new Color(getColor(item.status)).fade(0.25).hexa();
      return {
        type: 'bar' as const,
        name: item.status.toString(),
        stack: 'total',
        color,
        label: {
          // 8 was chosen by eye after running some tests in the Echarts editor.
          // From 8%, the bar gives enough space to the label.
          show: item.percentage > 8,
          fontWeight: 'bold',
          position: 'insideLeft',
          distance: 5,
          formatter: (params: any) => formatPercentage(params.value),
        },
        barWidth: '100%',
        data: [item.percentage],
      } as SeriesOption;
    });
  }
}
